/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import * as React from "react";
import styled, { css, keyframes } from "styled-components";
import { Check, X } from "lucide-react";

import { LIVE } from "@src/components/modules/LiveModule/liveUtils";
import type { Phase, WorkPhase } from "@src/utils/TransferProgress";

/* The phases of a run as a row of steps: done (slate check), current (cyan ring), to come (grey). */

const ring = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(70, 196, 242, 0.55); }
  100% { box-shadow: 0 0 0 8px rgba(70, 196, 242, 0); }
`;

const Row = styled.ol<{ $compact: boolean }>`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  align-items: flex-start;
  width: 100%;
  min-width: 0;
`;
const Step = styled.li`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  &:not(:first-child)::before {
    content: "";
    position: absolute;
    top: 10px;
    right: calc(50% + 14px);
    left: calc(-50% + 14px);
    height: 2px;
    border-radius: 1px;
    background: var(--line, ${LIVE.border});
  }
`;
type State = "done" | "current" | "todo" | "failed";
const Dot = styled.span<{ $state: State; $success: boolean }>`
  width: 22px;
  height: 22px;
  box-sizing: border-box;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
  ${p => {
    switch (p.$state) {
      case "done":
        return css`
          background: ${p.$success ? LIVE.green : LIVE.slate};
          color: #fff;
        `;
      case "current":
        return css`
          background: var(--t9-bg-surface);
          border: 2px solid ${LIVE.cyan};
          animation: ${ring} 1.8s ease-out infinite;
          &::after {
            content: "";
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: ${LIVE.cyan};
          }
          @media (prefers-reduced-motion: reduce) {
            animation: none;
          }
        `;
      case "failed":
        return css`
          background: ${LIVE.danger};
          color: #fff;
        `;
      default:
        return css`
          background: var(--t9-bg-surface);
          border: 2px solid ${LIVE.border};
        `;
    }
  }}
`;
const Name = styled.span<{ $state: State }>`
  margin-top: 6px;
  font-size: 11.5px;
  line-height: 15px;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  padding: 0 2px;
  font-weight: ${p => (p.$state === "current" || p.$state === "failed" ? 600 : 500)};
  color: ${p =>
    p.$state === "current"
      ? LIVE.text
      : p.$state === "failed"
        ? LIVE.danger
        : p.$state === "done"
          ? LIVE.textSecondary
          : LIVE.faint};
`;

type Props = {
  phases: WorkPhase[];
  phase: Phase;
  phaseIndex: number;
  compact?: boolean;
};

const PhaseStepper = ({ phases, phase, phaseIndex, compact = false }: Props) => {
  const success = phase === "Completed";
  const stopped = phase === "Failed" || phase === "Cancelled";
  return (
    <Row $compact={compact} aria-label={`Phase: ${phase}`}>
      {phases.map((p, i) => {
        let state: State = "todo";
        if (i < phaseIndex) {
          state = "done";
        } else if (i === phaseIndex) {
          state = stopped ? "failed" : success ? "done" : "current";
        }
        const lineDone = i <= phaseIndex && i > 0;
        return (
          <Step
            key={p}
            aria-current={state === "current" ? "step" : undefined}
            style={
              {
                "--line": lineDone ? (success ? LIVE.green : LIVE.slate) : LIVE.border,
              } as React.CSSProperties
            }
          >
            <Dot $state={state} $success={success}>
              {state === "done" ? <Check size={13} strokeWidth={3} /> : null}
              {state === "failed" ? <X size={13} strokeWidth={3} /> : null}
            </Dot>
            <Name $state={state}>{p}</Name>
          </Step>
        );
      })}
    </Row>
  );
};

export default PhaseStepper;
